import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api } from "../../lib/api";
import { SavedNote, XIcon } from "../shared/Icons";
import { DialogPanel } from "../shared/DialogPanel";
import { backdropClose } from "../shared/backdrop";
import { useUnsavedGuard } from "./unsavedGuard";
import { ModelSelector } from "./ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback } from "./ModelFallbackSection";

// Self-Service (Agentes Globais).
//  - Tipos e rótulos dos campos editáveis (o editor em si é builder/FieldEditor.tsx;
//    o cliente preenche no portal apps/ss-portal).
//  - Tools globais: painel no implantado (ocultar + campos editáveis) e editor
//    da tool vinculada (só campos editáveis) para agentes com import de global.
// Formato dos campos: carmillion_core/self_service.py.

export type FieldType = "text" | "textarea" | "slider" | "select" | "multi" | "bool" | "list" | "hours" | "faq";
export interface FieldOption { id: string; label: string; text: string }
export interface Field {
  id: string;  // identidade estável: answers do cliente são por id (key é só o chip)
  key: string; label: string; type: FieldType; description?: string; default?: any;
  limit?: number | null; min?: number; max?: number; step?: number; min_label?: string; max_label?: string;
  options?: FieldOption[]; max_choices?: number | null; display?: "cards" | "dropdown";
  other?: boolean; other_limit?: number | null;  // opção "Outros" + texto livre → chip campo.<key>_outro
  yes_text?: string; no_text?: string; max_items?: number | null;
  q_limit?: number | null; a_limit?: number | null;  // faq: teto de caracteres da pergunta/resposta
}

export const inp: React.CSSProperties = { padding: "7px 10px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
export const lbl: React.CSSProperties = { fontSize: 12, color: "var(--ink-soft)", marginBottom: 4, display: "block" };
const card: React.CSSProperties = { background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 };
const redBtn: React.CSSProperties = { padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" };
export const ghostBtn: React.CSSProperties = { padding: "6px 12px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" };
export const globalTag: React.CSSProperties = { fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 99, background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)", whiteSpace: "nowrap" };

export const TYPE_LABEL: Record<FieldType, string> = {
  text: "Texto simples", textarea: "Texto longo", slider: "Barra", select: "Lista (uma opção)", multi: "Múltipla escolha",
  bool: "Sim / Não", list: "Lista de itens", hours: "Horário de atendimento", faq: "Perguntas e respostas",
};

// ── Tools globais ─────────────────────────────────────────────────────────
export const MODEL_PATHS = ["model_provider_override", "model_name_override", "model_temperature_override", "model_reasoning_effort_override", "model_fallback"];
const JSON_COLS = new Set(["headers", "params", "body", "param_values", "prompts"]);
const BOOL_COLS = new Set(["qualifies_human", "ends_followup"]);
const NUM_COLS = new Set(["timeout", "cooldown_seconds", "pause_minutes"]);

function pathLabel(p: string): string {
  const [col, sub] = p.split(".");
  const base: Record<string, string> = {
    name: "Nome", url: "URL", method: "Método", code: "Código Python", description: "Descrição", timeout: "Timeout (s)",
    cooldown_seconds: "Limite de uso (cooldown, s)", cooldown_message: "Mensagem de cooldown", qualifies_human: "Qualifica para humano",
    ends_followup: "Encerra follow-up", pause_minutes: "Pausa da IA após responder (min)", headers: "Headers", params: "Params", body: "Body", param_values: "Fixos", prompts: "Prompts",
  };
  const sing: Record<string, string> = { headers: "Header", params: "Param", body: "Body", param_values: "Fixo", prompts: "Prompt", params_schema: "Descrição p/ IA do param" };
  return sub ? `${sing[col] ?? col} ${sub}` : (base[col] ?? col);
}

export function EditableInputs({ paths, values, onChange }: { paths: string[]; values: Record<string, any>; onChange: (path: string, v: any) => void }) {
  // JSON inteiro (headers/body/fixos...): texto local até virar JSON válido.
  const [raw, setRaw] = useState<Record<string, string>>({});
  if (!paths.length) return <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nada editável nesta tool global.</div>;
  const hasModel = paths.some((p) => MODEL_PATHS.includes(p));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {paths.filter((p) => !MODEL_PATHS.includes(p)).map((p) => {
        const [col, sub] = p.split(".");
        const v = values[p];
        if (BOOL_COLS.has(col)) {
          return (
            <label key={p} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
              <input type="checkbox" checked={!!v} onChange={(e) => onChange(p, e.target.checked)} style={{ accentColor: "var(--brand)" }} />
              {pathLabel(p)}
            </label>
          );
        }
        if (col === "params_schema" && sub) {
          // Param "Agente": só a descrição (instrução que a IA lê); tipo/required seguem o modelo.
          return (
            <div key={p}><label style={lbl}>{pathLabel(p)}</label>
              <textarea rows={2} style={{ ...inp, resize: "vertical" }} value={v?.description ?? ""} onChange={(e) => onChange(p, { type: "string", ...(v ?? {}), description: e.target.value })} /></div>
          );
        }
        if (col === "method") {
          return (
            <div key={p}><label style={lbl}>{pathLabel(p)}</label>
              <select style={inp} value={v ?? "POST"} onChange={(e) => onChange(p, e.target.value)}>
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select></div>
          );
        }
        if (JSON_COLS.has(col) && !sub) {
          const text = raw[p] ?? JSON.stringify(v ?? {}, null, 2);
          const bad = (() => { try { JSON.parse(text); return false; } catch { return true; } })();
          return (
            <div key={p}><label style={lbl}>{pathLabel(p)} (JSON)</label>
              <textarea rows={5} spellCheck={false} style={{ ...inp, resize: "vertical", fontFamily: "var(--font-mono)", fontSize: 12, borderColor: bad ? "var(--danger)" : undefined }}
                value={text} onChange={(e) => { setRaw((r) => ({ ...r, [p]: e.target.value })); try { onChange(p, JSON.parse(e.target.value)); } catch { /* espera JSON válido */ } }} /></div>
          );
        }
        if (col === "code") {
          return (
            <div key={p}><label style={lbl}>{pathLabel(p)}</label>
              <textarea rows={14} spellCheck={false} style={{ ...inp, resize: "vertical", fontFamily: "var(--font-mono)", fontSize: 12 }} value={v ?? ""} onChange={(e) => onChange(p, e.target.value)} /></div>
          );
        }
        const num = NUM_COLS.has(col);
        const long = col === "description" || col === "prompts" || col === "cooldown_message";
        return (
          <div key={p}>
            <label style={lbl}>{pathLabel(p)}</label>
            {long ? <textarea rows={2} style={{ ...inp, resize: "vertical" }} value={v ?? ""} onChange={(e) => onChange(p, e.target.value)} />
              : <input type={num ? "number" : "text"} style={inp} value={v ?? ""} onChange={(e) => onChange(p, num ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />}
          </div>
        );
      })}
      {hasModel && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, border: "1px solid var(--line)", borderRadius: 8, padding: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>Modelo da IA desta tool</span>
          <ModelSelector
            showMaxTokens={false}
            value={{ provider: values.model_provider_override ?? "", model_name: values.model_name_override ?? "", temperature: values.model_temperature_override ?? 0.7, reasoning_effort: values.model_reasoning_effort_override ?? "off" }}
            onChange={(n) => {
              onChange("model_provider_override", n.provider);
              onChange("model_name_override", n.model_name);
              onChange("model_temperature_override", n.temperature);
              onChange("model_reasoning_effort_override", n.reasoning_effort === "off" ? null : n.reasoning_effort);
            }}
          />
          <ModelFallbackSection value={fallbackToForm(values.model_fallback)} onChange={(f) => onChange("model_fallback", formToFallback(f))} hint="Usado se o modelo acima falhar." />
        </div>
      )}
    </div>
  );
}

// ── Versões da tool global (badge + popup) ─────────────────────────────────
export interface ToolVersion { version: number; note: string; at: string; by?: string | null }

export function VersionBadge({ versions }: { versions?: ToolVersion[] | null }) {
  const [open, setOpen] = useState(false);
  if (!versions?.length) return null;
  const last = versions[versions.length - 1];
  return (
    <>
      <button type="button" title={`v${last.version} · ${last.note}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{ ...globalTag, cursor: "pointer", background: "var(--surface-input)", color: "var(--ink-secondary)", borderColor: "var(--line-strong)" }}>
        v{last.version}
      </button>
      {open && (
        <div {...backdropClose(() => { setOpen(false); })} onClick={(e) => e.stopPropagation()} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setOpen(false)} label="Versões da tool" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 20, width: 480, maxWidth: "92vw", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Versões da tool global</span>
              <span style={{ flex: 1 }} />
              <button aria-label="Fechar" onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={14} /></button>
            </div>
            {[...versions].reverse().map((v) => (
              <div key={v.version} style={{ ...card, padding: 10, gap: 4 }}>
                <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--ink-soft)" }}>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--ink)", fontWeight: 600 }}>v{v.version}</span>
                  <span>{new Date(v.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })}</span>
                  {v.by && <span>· {v.by}</span>}
                </div>
                <div style={{ fontSize: 13, color: "var(--ink)", whiteSpace: "pre-wrap" }}>{v.note}</div>
              </div>
            ))}
          </DialogPanel>
        </div>
      )}
    </>
  );
}

// Dev (Modelo de Tool): checkbox "Editável" por campo.
// Self-Service: o que o CLIENTE vê da tool global no portal. Campo editável SEM rótulo aqui
// é "hard config" (só gestor/equipe); com rótulo vira input bonitinho no bloco do portal.
import type { PortalMeta } from "./builder/layout";
export type { PortalMeta };
/** Textarea que cresce com o conteúdo (sem barra de rolagem interna). */
export function AutoTextarea({ value, onChange, placeholder, minRows = 2 }: { value: string; onChange: (v: string) => void; placeholder?: string; minRows?: number }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [value]);
  return <textarea ref={ref} rows={minRows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={{ ...inp, resize: "none", overflow: "hidden", lineHeight: 1.5 }} />;
}

export function PortalFieldsEditor({ paths, labels, value, onChange }: {
  paths: string[]; labels: Record<string, string>; value: PortalMeta; onChange: (v: PortalMeta) => void;
}) {
  const setField = (p: string, patch: { label?: string; help?: string }) =>
    onChange({ ...value, fields: { ...(value.fields ?? {}), [p]: { ...(value.fields?.[p] ?? {}), ...patch } } });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div><label style={lbl}>Título no portal</label><input style={inp} value={value.title ?? ""} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="Busca no estoque" /></div>
      <div><label style={lbl}>Descrição no portal</label><AutoTextarea value={value.description ?? ""} onChange={(v) => onChange({ ...value, description: v })} placeholder="Como o agente procura veículos para o cliente" /></div>
      {paths.length > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", marginTop: 4 }}>Campos editáveis <span style={{ fontWeight: 400, color: "var(--ink-soft)" }}>· vazio = rótulo original</span></div>}
      {paths.map((p) => (
        <div key={p} style={{ ...card, padding: 12, gap: 8 }}>
          <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-dim)" }}>{labels[p] ?? p}</div>
          <div><label style={lbl}>Rótulo (o cliente vê)</label><input style={inp} value={value.fields?.[p]?.label ?? ""} onChange={(e) => setField(p, { label: e.target.value })} placeholder="vazio = rótulo original" /></div>
          <div><label style={lbl}>Ajuda (opcional)</label><AutoTextarea value={value.fields?.[p]?.help ?? ""} onChange={(v) => setField(p, { help: v })} placeholder="Explique em uma ou duas frases o que preencher" /></div>
        </div>
      ))}
    </div>
  );
}

export function EditableFieldsPicker({ candidates, value, onChange }: { candidates: { path: string; label: string; paths?: string[] }[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div style={{ ...card, background: "color-mix(in srgb, var(--warn-soft), transparent 55%)", borderColor: "var(--warn-line)" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>Tool global · campos editáveis pelo gestor <span style={{ fontWeight: 400, color: "var(--ink-soft)" }}>· o que não está marcado ele nem vê e segue o modelo ao vivo; o que está marcado é dele e NÃO se atualiza quando você mudar aqui</span></div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
        {candidates.map((c) => {
          const ps = c.paths ?? [c.path];
          const on = ps.every((p) => value.includes(p));
          return (
            <label key={c.path} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink)", cursor: "pointer" }}>
              <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? [...value.filter((x) => !ps.includes(x)), ...ps] : value.filter((x) => !ps.includes(x)))} style={{ accentColor: "var(--warn)" }} />
              {c.label}
            </label>
          );
        })}
        {candidates.length === 0 && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Preencha a tool para listar os campos.</span>}
      </div>
    </div>
  );
}

// Agente com tool global importada (linha própria com template_id): edita só os campos liberados.
export function GlobalToolEditor({ agentId, tool, onClose }: { agentId: string; tool: any; onClose: () => void }) {
  const qc = useQueryClient();
  const paths: string[] = tool.editable_fields ?? [];
  const [values, setValues] = useState<Record<string, any>>(() => Object.fromEntries(paths.map((p) => {
    const [col, sub] = p.split(".");
    const own = sub ? (tool[col] ?? {})[sub] : tool[col];
    return [p, own ?? tool.template_values?.[p]];  // campo novo/nunca preenchido → default do modelo
  })));
  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, any> = {};
      for (const p of paths) {
        const [col, sub] = p.split(".");
        if (sub) payload[col] = { ...(payload[col] ?? tool[col] ?? {}), [sub]: values[p] };
        else payload[col] = values[p];
      }
      return api.patch(`/agents/${agentId}/tools/${tool.id}`, payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-tools", agentId] }); qc.invalidateQueries({ queryKey: ["agent", agentId] }); onClose(); },
  });
  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={onClose} label={tool.name} style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 560, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{tool.name}</span>
          <span style={globalTag}>Tool global</span>
          <VersionBadge versions={tool.template_versions} />
          <span style={{ flex: 1 }} />
          <button aria-label="Fechar" onClick={onClose} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        {tool.description && !paths.includes("description") && <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>{tool.description}</div>}
        <EditableInputs paths={paths} values={values} onChange={(p, v) => setValues((s) => ({ ...s, [p]: v }))} />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={ghostBtn}>Cancelar</button>
          {paths.length > 0 && <button onClick={() => save.mutate()} disabled={save.isPending} style={redBtn}>{save.isPending ? "Salvando..." : "Salvar"}</button>}
        </div>
      </DialogPanel>
    </div>
  );
}

// Implantado: tools do global (ocultar/restaurar + campos editáveis), acima das próprias.
export function GlobalToolsPanel({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const tools: { id: string; name: string; description: string | null; kind: string; editable_fields: string[]; values: Record<string, any>; versions?: ToolVersion[] }[] = agent.global_form?.tools ?? [];
  const ss = agent.self_service ?? {};
  const [hidden, setHidden] = useState<string[]>(ss.hidden_tools ?? []);
  const [overrides, setOverrides] = useState<Record<string, Record<string, any>>>(ss.tool_overrides ?? {});
  const snap = (h: string[], o: any) => JSON.stringify([h, o]);
  const [saved, setSaved] = useState(() => snap(ss.hidden_tools ?? [], ss.tool_overrides ?? {}));
  const save = useMutation({
    mutationFn: () => api.patch(`/agents/${agent.id}`, { self_service: { ...ss, hidden_tools: hidden, tool_overrides: overrides } }),
    onSuccess: () => { setSaved(snap(hidden, overrides)); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
  });
  useUnsavedGuard(snap(hidden, overrides) !== saved, save.mutateAsync);
  if (tools.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 12, borderBottom: "1px solid var(--line)" }}>
      <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>{tools.length} tool{tools.length !== 1 ? "s" : ""} do Agente Global <span style={{ color: "var(--ink-muted)" }}>· mantidas pela equipe; você pode ocultar ou ajustar o que foi liberado</span></span>
      {tools.map((t) => {
        const off = hidden.includes(t.id);
        return (
          <div key={t.id} style={{ ...card, opacity: off ? 0.5 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 500, fontSize: 13, color: "var(--ink)" }}>{t.name}</span>
              <span style={globalTag}>Tool global</span>
              <VersionBadge versions={t.versions} />
              {off && <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 99, background: "var(--surface-input)", color: "var(--ink-muted)" }}>oculta</span>}
              <span style={{ flex: 1 }} />
              <button onClick={() => setHidden((h) => (off ? h.filter((x) => x !== t.id) : [...h, t.id]))} style={ghostBtn}>{off ? "Restaurar" : "Ocultar"}</button>
            </div>
            {t.description && <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>{t.description}</div>}
            {!off && t.editable_fields.length > 0 && (
              <EditableInputs paths={t.editable_fields} values={{ ...t.values, ...(overrides[t.id] ?? {}) }}
                onChange={(p, v) => setOverrides((o) => ({ ...o, [t.id]: { ...(o[t.id] ?? {}), [p]: v } }))} />
            )}
          </div>
        );
      })}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => save.mutate()} disabled={save.isPending} style={redBtn}>{save.isPending ? "Salvando..." : "Salvar tools globais"}</button>
        <SavedNote show={save.isSuccess} />
      </div>
    </div>
  );
}
